import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Breadcrumb } from 'react-bootstrap';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api'; // Assuming you have an api service configured
import ReplyList from '../components/forum/ReplyList'; // Import the new component
import CreateReplyForm from '../components/forum/CreateReplyForm'; // Import the reply form

// Define interfaces for the data structures
interface User {
    id: number;
    name: string;
}

interface Category {
    id: number;
    name: string;
}

// Interface for a single reply
interface Reply {
    id: number;
    content: string;
    createdAt: string;
    author: User;
    // Add other fields if needed, e.g., voteCount
}

// Interface for the main topic data
interface Topic {
    id: number;
    title: string;
    content: string;
    createdAt: string;
    author: User;
    category: Category;
    replies: Reply[];
    isLocked: boolean;
}

// Define the structure returned by the create reply API
interface NewReply extends Reply {} // Assuming NewReply has the same structure as Reply for now

// Type for successful topic fetch
interface TopicSuccessResponse {
    success: true;
    data: Topic;
}

// Type for failed topic fetch (adjust based on actual API error structure)
interface TopicErrorResponse {
    success: false;
    message: string;
    // include other potential error fields
}

// Union type for the API response
type TopicApiResponse = TopicSuccessResponse | TopicErrorResponse;

const TopicDetailPage: React.FC = () => {
    const { topicId } = useParams<{ topicId: string }>();
    const [topic, setTopic] = useState<Topic | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isLocked, setIsLocked] = useState<boolean>(false); // State for topic locked status

    // Function to fetch topic data
    const fetchTopic = async () => {
        // setLoading(true); // Set loading only on initial load?
        setError(null);
        try {
            // Use the union type for the response
            const response = await api.get<TopicApiResponse>(`/forum/topics/${topicId}`);

            if (response.data.success) {
                setTopic(response.data.data);
                setIsLocked(response.data.data.isLocked || false); 
            } else {
                 // Now response.data.message is recognized because success is false
                throw new Error(response.data.message || 'Failed to fetch topic (API error).');
            }
        } catch (err: any) {
            console.error("Error fetching topic:", err);
            // More robust error message extraction
            let errorMessage = 'An error occurred while fetching the topic.';
            if (err.response?.data && typeof err.response.data === 'object' && 'message' in err.response.data) {
                 errorMessage = err.response.data.message; // Use message from API error response
            } else if (err.message) {
                 errorMessage = err.message; // Use error object message
            }
            setError(errorMessage);
        } finally {
             if (loading) setLoading(false); // Turn off initial loading spinner
        }
    };

    useEffect(() => {
        if (topicId) {
            setLoading(true); // Set initial loading state
            fetchTopic();
        } else {
            setError("Topic ID is missing.");
            setLoading(false);
        }
    }, [topicId]); // Re-fetch if topicId changes

    // Handler for successful reply submission
    const handleReplySubmitSuccess = (newReply: NewReply) => {
        // Update the topic state by adding the new reply to the existing list
        setTopic(prevTopic => {
            if (!prevTopic) return null;
            return {
                ...prevTopic,
                replies: [...prevTopic.replies, newReply]
            };
        });
        // Optional: Scroll to the new reply?
        // setTimeout(() => {
        //     const replyElement = document.getElementById(`reply-${newReply.id}`);
        //     replyElement?.scrollIntoView({ behavior: 'smooth' });
        // }, 100);
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading topic...</span>
                    </Spinner>
                    <p>Loading topic...</p>
                </div>
            );
        }

        if (error) {
            return <Alert variant="danger">Error: {error}</Alert>;
        }

        if (!topic) {
            return <Alert variant="warning">Topic not found.</Alert>;
        }

        // Basic content rendering - enhance later (e.g., markdown)
        return (
            <>
                <Card className="mb-3">
                    <Card.Header>
                        <Row>
                            <Col>
                                Posted in <Link to="/forum" state={{ categoryId: topic.category.id }} className="fw-bold">{topic.category.name}</Link>
                            </Col>
                            <Col xs="auto" className="text-muted">
                                {formatDistanceToNow(new Date(topic.createdAt), { addSuffix: true })}
                            </Col>
                         </Row>
                    </Card.Header>
                    <Card.Body>
                        <Card.Title as="h2">{topic.title}</Card.Title>
                        <Card.Subtitle className="mb-3 text-muted">
                            By <Link to={`/profile/${topic.author.id}`}>{topic.author.name}</Link> 
                        </Card.Subtitle>
                        {/* Render topic content - Consider using a Markdown renderer */}
                        <div style={{ whiteSpace: 'pre-wrap' }}> 
                             {topic.content}
                        </div>
                    </Card.Body>
                </Card>

                {/* Replies Section */}
                <h3 className="mt-4 mb-3">Replies ({topic.replies?.length || 0})</h3> 
                <ReplyList replies={topic.replies} />
                
                 {/* Add Create Reply Form if topic is not locked */}
                 {!isLocked && topicId && (
                     <CreateReplyForm 
                        topicId={parseInt(topicId, 10)} // Ensure topicId is a number
                        onReplySubmitSuccess={handleReplySubmitSuccess} 
                     />
                 )}
                 {/* Show locked message if topic is locked */}
                 {isLocked && (
                     <Alert variant="secondary" className="text-center mt-4">
                         <i className="fas fa-lock me-1"></i> This topic is locked. No further replies are allowed.
                     </Alert>
                 )}
            </>
        );
    };

    return (
        <Container fluid className="py-3">
             <Breadcrumb>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/forum" }}>Forum</Breadcrumb.Item>
                {topic && <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/forum", state: { categoryId: topic.category.id } }}>{topic.category.name}</Breadcrumb.Item>}
                <Breadcrumb.Item active>{loading ? 'Loading Topic...' : (topic?.title || 'Topic Detail')}</Breadcrumb.Item>
            </Breadcrumb>

            {renderContent()}
        </Container>
    );
};

export default TopicDetailPage; 